import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { queryKeys } from 'src/api/db/queryKeys'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { ensureQuoteItemCanvasStub } from 'src/api/db/utils/ensureQuoteItemCanvasStub'
import { useSupabaseClient } from 'src/api/supabase'

/** Ensures product quote items have a decoration + design stub so they appear on the canvas. */
export const useEnsureQuoteItemsOnCanvas = (
  quoteId: number | undefined,
  quoteItems: QuoteItem_[] | undefined
) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const repairingRef = useRef(new Set<number>())
  const failedRef = useRef(new Set<number>())

  useEffect(() => {
    if (!quoteId || !quoteItems?.length) return

    const itemsNeedingStub = quoteItems.filter(
      (item) =>
        item.type === 'product' &&
        !failedRef.current.has(item.id) &&
        !(item.quote_sub_item ?? []).some((sub) => sub.type === 'decoration')
    )

    if (!itemsNeedingStub.length) return

    let cancelled = false

    void (async () => {
      for (const item of itemsNeedingStub) {
        if (cancelled || repairingRef.current.has(item.id)) continue
        repairingRef.current.add(item.id)

        try {
          await ensureQuoteItemCanvasStub(supabase, {
            quoteItemId: item.id,
            quantity: item.quantity ?? 1,
          })
        } catch (error) {
          console.error(
            'Failed to create canvas stub for quote item',
            item.id,
            error
          )
          failedRef.current.add(item.id)
          repairingRef.current.delete(item.id)
        }
      }

      if (!cancelled) {
        await queryClient.refetchQueries({
          queryKey: [queryKeys.quoteItems, quoteId],
        })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [quoteId, quoteItems, queryClient, supabase])
}
